import React from 'react';
import { GitMerge, Phone, Mail } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';

/**
 * Merge confirmation dialog for two surgeon records.
 *
 * Same keep/discard pattern as MergeClinicModal, but merges a person:
 * union of clinic_ids (site links) and contacts (deduped server-side on
 * type+value), plus editable merged name/specialty/qualifications.
 * onMerge posts to /api/surgeons-db/surgeons/merge.
 * Written with `var`/function-expressions rather than the arrow/const
 * style of the other components — style only, same behaviour.
 * Radix Dialog: the null mergeTarget early-return stays (nothing renders
 * closed); Escape/overlay-click route to onCancel. z-60 keeps it stacked
 * above the z-50 duplicate-finder modal that spawned it.
 */

var ContactList = function(props) {
  var contacts = props.contacts || [];
  return (
    <div className="space-y-0.5 mt-1">
      {contacts.slice(0, 4).map(function(c, i) {
        var Icon = c.type === 'email' ? Mail : Phone;
        return (
          <div key={i} className="flex items-center space-x-1 text-xs text-gray-500">
            <Icon className="h-3 w-3 text-gray-400 shrink-0" />
            <span className="truncate">{c.value}</span>
          </div>
        );
      })}
    </div>
  );
};

var SurgeonMergeModal = function(props) {
  var mergeTarget = props.mergeTarget;
  var mergeForm = props.mergeForm;
  var setMergeForm = props.setMergeForm;
  var onCancel = props.onCancel;
  var onMerge = props.onMerge;

  if (!mergeTarget) return null;

  var keep = mergeTarget.keep;
  var discard = mergeTarget.discard;

  function fullName(s) {
    return [s.title, s.first_name, s.last_name].filter(Boolean).join(' ');
  }

  function clinicCount(s) {
    return (s.clinic_ids || []).length;
  }

  function handleChange(field, value) {
    setMergeForm(function(f) {
      var next = Object.assign({}, f);
      next[field] = value;
      return next;
    });
  }

  return (
    <Dialog open onOpenChange={function(o) { if (!o) onCancel(); }}>
      <DialogContent className="max-w-2xl p-6 overflow-y-auto z-[60]" overlayClassName="bg-opacity-60 z-[60]" style={{maxHeight:'90vh'}}>
        <div className="flex items-center space-x-2 mb-5">
          <GitMerge className="h-5 w-5 text-green-600" />
          <DialogTitle className="text-base">Merge Surgeon Records</DialogTitle>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="border-2 border-green-400 rounded-xl p-3 bg-green-50">
            <div className="text-xs font-bold text-green-700 uppercase mb-1">Keep</div>
            <div className="text-sm font-semibold text-gray-900">{fullName(keep)}</div>
            <div className="text-xs text-gray-500">{keep.specialty}</div>
            <div className="text-xs text-gray-400">{keep.qualifications}</div>
            <div className="text-xs text-gray-400 mt-1">{clinicCount(keep)} clinic{clinicCount(keep) !== 1 ? 's' : ''}</div>
            <ContactList contacts={keep.contacts} />
          </div>
          <div className="border-2 border-red-300 rounded-xl p-3 bg-red-50">
            <div className="text-xs font-bold text-red-600 uppercase mb-1">Discard</div>
            <div className="text-sm font-semibold text-gray-900">{fullName(discard)}</div>
            <div className="text-xs text-gray-500">{discard.specialty}</div>
            <div className="text-xs text-gray-400">{discard.qualifications}</div>
            <div className="text-xs text-gray-400 mt-1">{clinicCount(discard)} clinic{clinicCount(discard) !== 1 ? 's' : ''}</div>
            <ContactList contacts={discard.contacts} />
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-4 text-xs text-blue-700">
          Clinics and contacts from both records will be combined. Duplicate contacts (same type + value) are deduplicated automatically.
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">First name</label>
            <input value={mergeForm.merged_first_name || ''}
              onChange={function(e) { handleChange('merged_first_name', e.target.value); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Last name</label>
            <input value={mergeForm.merged_last_name || ''}
              onChange={function(e) { handleChange('merged_last_name', e.target.value); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Specialty</label>
            <input value={mergeForm.merged_specialty || ''}
              onChange={function(e) { handleChange('merged_specialty', e.target.value); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Qualifications</label>
            <input value={mergeForm.merged_qualifications || ''}
              onChange={function(e) { handleChange('merged_qualifications', e.target.value); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
          </div>
        </div>

        <div className="flex space-x-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">Cancel</button>
          <button onClick={onMerge} className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center justify-center space-x-1">
            <GitMerge className="h-4 w-4" /><span>Merge Records</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SurgeonMergeModal;
